import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const AGE_RANGES = ["6-8", "9-11", "12-14", "15-17", "18+"];
const GRADE_BANDS = ["Elementary", "Middle School", "High School", "Adult"];

const StudentRegisterPage = () => {
  const navigate = useNavigate();
  const { registerStudent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    age_range: "",
    grade_band: "",
    guardian_email: "",
    social_media_waiver: false,
    waiver_signature: ""
  });

  const isMinor = formData.age_range && formData.age_range !== "18+";

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (isMinor && !formData.guardian_email) {
      toast.error("Guardian email is required for students under 18");
      return;
    }

    if (formData.social_media_waiver) {
      if (!formData.waiver_signature.trim()) {
        toast.error("Please type your full name to sign the waiver");
        return;
      }
      if (formData.waiver_signature.trim() !== formData.name.trim()) {
        toast.error("Waiver signature must exactly match your Full Name above");
        return;
      }
    }

    setLoading(true);
    try {
      await registerStudent({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        age_range: formData.age_range,
        grade_band: formData.grade_band,
        guardian_email: isMinor ? formData.guardian_email : null,
        social_media_waiver: formData.social_media_waiver,
        waiver_signature: formData.social_media_waiver ? formData.waiver_signature : null
      });
      toast.success("Registration successful!");
      navigate("/courses");
    } catch (error) {
      const msg = error.response?.data?.detail || "Registration failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-6">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="brutalist-card p-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="register-title">Create Account</h1>
          <p className="text-[#4A4A4A] mb-8">Join Virtually Verbal and start learning!</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Full Name *</Label>
              <Input
                data-testid="register-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="brutalist-input mt-2"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Email *</Label>
              <Input
                data-testid="register-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="brutalist-input mt-2"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider">Age Range *</Label>
                <select
                  data-testid="register-age-range"
                  value={formData.age_range}
                  onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                  className="brutalist-input mt-2 w-full"
                  required
                >
                  <option value="">Select...</option>
                  {AGE_RANGES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider">Grade Band *</Label>
                <select
                  data-testid="register-grade-band"
                  value={formData.grade_band}
                  onChange={(e) => setFormData({ ...formData, grade_band: e.target.value })}
                  className="brutalist-input mt-2 w-full"
                  required
                >
                  <option value="">Select...</option>
                  {GRADE_BANDS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>

            {isMinor && (
              <div className="bg-[#FBE7C6] border-2 border-[#0A0A0A] rounded-lg p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                  <span className="font-medium">Guardian consent required</span>
                </div>
                <Label className="text-sm font-bold uppercase tracking-wider">Guardian Email *</Label>
                <Input
                  data-testid="register-guardian-email"
                  type="email"
                  value={formData.guardian_email}
                  onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                  placeholder="parent@example.com"
                  className="brutalist-input mt-2 bg-white"
                  required={isMinor}
                />
              </div>
            )}

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Password *</Label>
              <div className="relative">
                <Input
                  data-testid="register-password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="brutalist-input mt-2 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-[#4A4A4A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Confirm Password *</Label>
              <Input
                data-testid="register-confirm-password"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Confirm your password"
                className="brutalist-input mt-2"
                required
              />
            </div>

            {/* Social Media Waiver */}
            <div className="border-2 border-[#0A0A0A] rounded-lg p-4 bg-[#F0EFEB]">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid="register-waiver"
                  checked={formData.social_media_waiver}
                  onCheckedChange={(checked) => setFormData({ ...formData, social_media_waiver: checked })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium">Social Media Consent (Optional)</span>
                  <p className="text-sm text-[#4A4A4A] mt-1">
                    I consent to having my successes and photos shared on Virtually Verbal's social media.
                  </p>
                </div>
              </label>
              
              {formData.social_media_waiver && (
                <div className="mt-4">
                  <Label className="text-sm font-bold uppercase tracking-wider">Type Your Name to Sign</Label>
                  <Input
                    data-testid="register-waiver-signature"
                    value={formData.waiver_signature}
                    onChange={(e) => setFormData({ ...formData, waiver_signature: e.target.value })}
                    placeholder="Type your full name"
                    className="brutalist-input mt-2 bg-white italic"
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Creating...</> : "Create Account"}
            </button>
          </form>

          <p className="text-center mt-6 text-[#4A4A4A]">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-[#0A0A0A] hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentRegisterPage;
